import React, { Suspense, lazy } from 'react'
import { makeStyles, Toolbar, useTheme } from '@material-ui/core'
import { KeyboardArrowLeft } from '@material-ui/icons'
import { useLocation } from 'react-router'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { NASEF_SLUG, Param, Path } from '@plvs/const'
import { ApiEnvBadge } from '@plvs/rally/components/badge'
import { Box, FlexSpacer, Hidden } from '@plvs/respawn/features/layout'
import { rallyEnv } from '@plvs/rally/env'
import { Colors } from '@plvs/rally/themes/colors'
import { MUIThemeModeEnum } from '@playvs-inc/nexus-theme'
import { NxSkeletonLoader, NxTypography } from '@playvs-inc/nexus-components'
import NasefLight from '@plvs/rally/assets/nasef/NasefLight.png'
import NasefDark from '@plvs/rally/assets/nasef/NasefDark.png'

const Link = lazy(() =>
  import('@material-ui/core').then((m) => ({ default: m.Link }))
)
const MuiAppBar = lazy(() =>
  import('@material-ui/core').then((m) => ({ default: m.AppBar }))
)

const ElevateOnScroll = lazy(() => import('./ElevateOnScroll'))
const LogoMark = lazy(() =>
  import('@plvs/rally/components/icon').then((m) => ({ default: m.LogoMark }))
)
const LogoWordMark = lazy(() =>
  import('@plvs/rally/components/icon').then((m) => ({
    default: m.LogoWordMark,
  }))
)

interface AppBarProps {
  isInApp?: boolean
  leftContent?: React.ReactNode
  rightContent?: React.ReactNode
}

const useStyles = makeStyles(() => ({
  header: {
    width: '100vw',
    zIndex: ('1400 !important' as unknown) as number, // Needed for manage match menu take over on mobile
    '.MuiAppBar-root': {
      zIndex: ('1400 !important' as unknown) as number, // Needed for manage match menu take over on mobile
    },
  },
  desktopLogo: {
    width: '124px',
    height: '32px',
  },
  mobileLogo: {
    width: '32px',
    height: '32px',
    color: Colors.TEMPNewOrange,
  },
  backContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  backArrow: {
    backgroundColor: `${Colors.Grey6}`,
    borderRadius: '50%',
    height: 40,
    width: 40,
    display: 'flex',
    border: `2px solid ${Colors.Grey4}`,
    color: Colors.Black,
    marginRight: '12px',
  },
}))

const Logo: React.FC = () => {
  const classes = useStyles()

  return (
    <>
      <Hidden smDown>
        <Suspense fallback={<NxSkeletonLoader />}>
          <LogoWordMark className={classes.desktopLogo} />
        </Suspense>
      </Hidden>
      <Hidden mdUp>
        <Suspense fallback={<NxSkeletonLoader />}>
          <LogoMark className={classes.mobileLogo} />
        </Suspense>
      </Hidden>
    </>
  )
}

export const AppBar: React.FC<AppBarProps> = ({
  isInApp,
  leftContent,
  rightContent,
}) => {
  const theme = useTheme()
  const location = useLocation()
  const classes = useStyles()
  const flags = useFlags()

  const urlSearchParams = new URLSearchParams(location.search)
  const redirect = urlSearchParams.get(Param.Redirect) || ''
  const text = urlSearchParams.get(Param.Text) || 'Back'
  const isNasefSignUp =
    urlSearchParams.get(Param.UTM) === NASEF_SLUG && flags.nasefUx
  const isDarkMode = theme.palette.type === MUIThemeModeEnum.Dark
  // During onboarding, we want to prevent users from navigating
  // off the onboarding flow.
  const isOnboarding =
    location.pathname.includes(Path.SpawnPoint) ||
    location.pathname.includes(Path.MoreDetails) ||
    location.pathname.includes(Path.Forbidden)

  const NASEFMode = isDarkMode ? NasefDark : NasefLight

  const defaultLogo = isOnboarding ? <Logo /> : null

  const logoToShow = isNasefSignUp ? (
    <img alt="nasef-logo-nav" height={38} src={NASEFMode} width={125} />
  ) : (
    defaultLogo
  )

  const showApiEnvBadge =
    Boolean(rallyEnv.API_ENV) && rallyEnv.API_ENV !== 'production'
  const content = (
    <Suspense fallback={<NxSkeletonLoader />}>
      <ElevateOnScroll>
        <MuiAppBar className={classes.header} color="inherit" position="sticky">
          <Toolbar variant="regular">
            {leftContent}
            {redirect ? (
              <Suspense fallback={<NxSkeletonLoader />}>
                <Link color="inherit" href={redirect}>
                  <Box className={classes.backContainer}>
                    <KeyboardArrowLeft className={classes.backArrow} />
                    <NxTypography variant="subtitle2">{text}</NxTypography>
                  </Box>
                </Link>
              </Suspense>
            ) : (
              logoToShow
            )}

            {showApiEnvBadge && <ApiEnvBadge apiEnv={rallyEnv.API_ENV} />}
            <FlexSpacer />
            {rightContent}
          </Toolbar>
        </MuiAppBar>
      </ElevateOnScroll>
    </Suspense>
  )

  return (
    <>
      <Hidden mdUp>{content}</Hidden>
      <Hidden smDown>{!isInApp && content}</Hidden>
    </>
  )
}
